// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createArtifact } from '../services/sopFactoryService';
import { fromProcedureDetail } from './fromProcedureDetail';
import { toProcedureDetail } from './toProcedureDetail';
import type { ProcedureDetail } from '../types/procedures';

export interface CreateProcedureView {
  create: (procedure: ProcedureDetail) => Promise<ProcedureDetail>;
  isCreating: boolean;
  error: string | null;
}

/**
 * Wraps `createArtifact()` (no backend route yet — see that function's own
 * docstring) with the `ProcedureDetail`<->`Artifact` mapping and cache
 * invalidation, so `CreateProcedurePage` doesn't need to know about either.
 * Unlike `useSaveProcedure`, there's no existing `Artifact` to use as the
 * mapping's base — a brand-new draft has none of the fields the guided form
 * never edits (`payload_ref`/`relationships`/`provenance`/`approval`) yet, so
 * `fromProcedureDetail` gets an empty object instead.
 */
export function useCreateProcedure(): CreateProcedureView {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (procedure: ProcedureDetail) => {
      const payload = fromProcedureDetail(procedure, {});
      const created = await createArtifact(payload);
      return toProcedureDetail(created);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-center'] });
    },
  });

  return {
    create: procedure => mutation.mutateAsync(procedure),
    isCreating: mutation.isPending,
    error: mutation.isError ? 'Unable to create this procedure.' : null,
  };
}
